import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { useStrategies } from "@src/hooks/redux";

/**
 * Common settings that can be loaded from URL params.
 * Maps to CommonSettingsBlock fields.
 */
export interface CommonSettingsFromUrl {
  strategyId: number;
  exchangeName: string;
  instrument: string;
  timeframe: string;
}

/**
 * URL param names for common settings.
 * - strategy_name: resolved to strategy_id via strategies list
 * - strategy_id: direct numeric ID
 * - exchange_name: exchange identifier
 * - instrument: symbol/instrument name
 * - timeframe: candle timeframe
 */
const URL_PARAMS = {
  STRATEGY_ID: "strategy_id",
  STRATEGY_NAME: "strategy_name",
  EXCHANGE_NAME: "exchange_name",
  INSTRUMENT: "instrument",
  TIMEFRAME: "timeframe",
} as const;

interface UseCommonSettingsUrlParamsOptions {
  /** Skip loading if true (e.g., when viewing existing entity) */
  skip?: boolean;
  /** Callback when URL params are loaded */
  onLoad?: (settings: Partial<CommonSettingsFromUrl>) => void;
}

/**
 * Hook to load common settings form values from URL search params.
 * Supports strategy resolution by name or ID.
 *
 * Example URLs:
 * - /backtest/new?strategy_name=RangePrintStrategy&exchange_name=bybit&instrument=XMRUSDT_PERP
 * - /optimization/new?strategy_id=8&exchange_name=bybit&instrument=XMRUSDT_PERP&timeframe=15m
 */
export const useCommonSettingsUrlParams = (options: UseCommonSettingsUrlParamsOptions = {}) => {
  const { skip = false, onLoad } = options;
  const [searchParams] = useSearchParams();
  const { strategies } = useStrategies();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current || skip) return;
    if (strategies.length === 0) return; // Wait for data

    const strategyIdParam = searchParams.get(URL_PARAMS.STRATEGY_ID);
    const strategyNameParam = searchParams.get(URL_PARAMS.STRATEGY_NAME);
    const exchangeName = searchParams.get(URL_PARAMS.EXCHANGE_NAME);
    const instrument = searchParams.get(URL_PARAMS.INSTRUMENT);
    const timeframe = searchParams.get(URL_PARAMS.TIMEFRAME);

    // Need at least one identifying param to proceed
    if (!strategyIdParam && !strategyNameParam) return;

    loadedRef.current = true;

    // Resolve strategy: by ID > by name
    let strategyId = 0;
    if (strategyIdParam) {
      strategyId = Number(strategyIdParam);
    } else if (strategyNameParam) {
      const found = strategies.find((s) => s.name === strategyNameParam);
      strategyId = found?.id || 0;
    }

    const settings: Partial<CommonSettingsFromUrl> = {};

    if (strategyId) settings.strategyId = strategyId;
    if (exchangeName) settings.exchangeName = exchangeName;
    if (instrument) settings.instrument = instrument;
    if (timeframe) settings.timeframe = timeframe;

    console.log("[useCommonSettingsUrlParams] Loaded from URL:", {
      strategyId,
      strategyName: strategyNameParam,
      exchangeName,
      instrument,
      timeframe,
    });

    onLoad?.(settings);
  }, [searchParams, strategies, skip, onLoad]);

  // Check if URL has any common settings params
  const hasUrlParams =
    searchParams.has(URL_PARAMS.STRATEGY_ID) ||
    searchParams.has(URL_PARAMS.STRATEGY_NAME) ||
    searchParams.has(URL_PARAMS.EXCHANGE_NAME) ||
    searchParams.has(URL_PARAMS.INSTRUMENT);

  return { hasUrlParams };
};
